import { z } from 'zod';
import { ConfidenceBand, DecisionStatus, IssueSeverity } from './vocabularies';
import { IsoDate, Timestamp, Uuid } from './envelope';
import { ChartPayload, MetricValue } from './metric';
import { EntityRef, OwnerRef } from './read-models';

/**
 * Read models for governance collaboration, watching and reporting (D13).
 *
 * Four projections behind UC-13-096 (comments, actions, decisions), UC-13-097
 * (favourites, watchlists, notifications), UC-13-098 (scheduled and on-demand
 * reports) and UC-13-094 (taxonomy and policy versions).
 *
 * The load-bearing modelling choice in this file is that a comment, an action
 * and a decision are three record types, not one record with a `kind` column.
 * A comment is an utterance: it has an author and a time and nothing else is
 * required of it. An action is a commitment: it has an owner, a due date and a
 * terminal state. A decision is an exercise of authority: it has a decider, an
 * effective date, a rationale and — the field that most systems omit and most
 * auditors ask for first — a stated way to reverse it. Collapsing them into one
 * shape would mean either making `owner`, `dueOn`, `decidedOn` and `rationale`
 * all optional, which is the same as not requiring any of them, or filling them
 * with placeholders, which is worse. They share a screen because they share a
 * subject; they do not share a schema.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Shared fragments
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Who a record is about. Nullable because governance records outlive their
 * subjects: a decision to retire an application is still readable after the
 * application is gone, and pointing it at a tombstone is more honest than
 * dropping the record.
 */
export const CollaborationSubject = z.object({
  ref: EntityRef.nullable(),
  /** Rendered when `ref` is null — never a dash. */
  missingReason: z.string().nullable(),
});
export type CollaborationSubject = z.infer<typeof CollaborationSubject>;

// ─────────────────────────────────────────────────────────────────────────────
// 1. Collaboration workspace — comments, actions, decisions   (UC-13-096)
// ─────────────────────────────────────────────────────────────────────────────

/** Who may read a comment. Restricted threads are listed but not quoted. */
export const CommentVisibility = z.enum(['Team', 'Governance', 'Restricted']);
export type CommentVisibility = z.infer<typeof CommentVisibility>;

export const CommentRecord = z.object({
  id: Uuid,
  businessCode: z.string(),
  subject: CollaborationSubject,
  author: OwnerRef,
  postedAt: Timestamp,
  /**
   * Null when the caller may not read the thread. The row still appears — a
   * reader must be able to see that a conversation exists on a record they
   * govern, even when they may not read it — and `withheldReason` says why.
   */
  body: z.string().nullable(),
  withheldReason: z.string().nullable(),
  visibility: CommentVisibility,
  replyCount: z.number().int().nonnegative(),
  /** Who has been drawn in. Drives the "needs a reply from you" grouping. */
  mentions: z.array(z.string()),
  resolvedAt: Timestamp.nullable(),
  resolvedBy: z.string().nullable(),
  /** Set when the thread produced a commitment or a ruling. */
  raisedActionCode: z.string().nullable(),
  raisedDecisionCode: z.string().nullable(),
});
export type CommentRecord = z.infer<typeof CommentRecord>;

export const ActionStatus = z.enum(['Open', 'InProgress', 'Blocked', 'Done', 'Cancelled']);
export type ActionStatus = z.infer<typeof ActionStatus>;

export const ActionRecord = z.object({
  id: Uuid,
  businessCode: z.string(),
  title: z.string(),
  subject: CollaborationSubject,
  /** Null only when the record genuinely has no owner; that is a defect to fix. */
  owner: OwnerRef.nullable(),
  unownedReason: z.string().nullable(),
  raisedBy: z.string(),
  raisedOn: IsoDate,
  dueOn: IsoDate.nullable(),
  noDueDateReason: z.string().nullable(),
  status: ActionStatus,
  /** Required whenever `status` is `Blocked`; the board is unreadable without it. */
  blockedReason: z.string().nullable(),
  closedOn: IsoDate.nullable(),
  severity: IssueSeverity,
  /** Provenance of the commitment: which comment or decision produced it. */
  originCommentCode: z.string().nullable(),
  originDecisionCode: z.string().nullable(),
});
export type ActionRecord = z.infer<typeof ActionRecord>;

/**
 * A decision of record.
 *
 * `reversalPath` is mandatory and is not free text about "review later": it
 * names the mechanism by which this decision can be undone and who may invoke
 * it. A governed decision nobody knows how to reverse is a decision that gets
 * worked around instead of revisited.
 */
export const DecisionRecord = z.object({
  id: Uuid,
  businessCode: z.string(),
  title: z.string(),
  subject: CollaborationSubject,
  status: DecisionStatus,
  /** Null while the decision is still in flight — never back-filled with today. */
  decidedOn: IsoDate.nullable(),
  decidedBy: OwnerRef.nullable(),
  /** Business effective date. Differs from `decidedOn` more often than not. */
  effectiveFrom: IsoDate.nullable(),
  rationale: z.string(),
  reversalPath: z.string(),
  /** The version chain. A decision is amended by superseding it, never by edit. */
  supersedesCode: z.string().nullable(),
  supersededByCode: z.string().nullable(),
  /** Records cited when the decision was taken; count only, the list is elsewhere. */
  evidenceCount: z.number().int().nonnegative(),
  confidence: ConfidenceBand,
  linkedActionCodes: z.array(z.string()),
  /** Where the approval itself is governed — the command centre, not here. */
  approvalRoute: z.string(),
});
export type DecisionRecord = z.infer<typeof DecisionRecord>;

export const CollaborationWorkspaceView = z.object({
  kpis: z.array(MetricValue),
  comments: z.array(CommentRecord),
  actions: z.array(ActionRecord),
  decisions: z.array(DecisionRecord),
  /**
   * The approval queue and the audit trail live in the governance command
   * centre. This view carries the route to them rather than a second copy of
   * their rows, because two consoles counting the same queue disagree within a
   * week.
   */
  governanceConsoleRoute: z.string(),
  auditTrailRoute: z.string(),
});
export type CollaborationWorkspaceView = z.infer<typeof CollaborationWorkspaceView>;

// ─────────────────────────────────────────────────────────────────────────────
// 2. Watchlist — favourites, watchlists, notifications        (UC-13-097)
// ─────────────────────────────────────────────────────────────────────────────

export const FavouriteRow = z.object({
  id: Uuid,
  subject: EntityRef,
  pinnedOn: IsoDate,
  /** Why this reader pinned it. Their own words, not a derived label. */
  note: z.string().nullable(),
  /** Health/state word supplied by the subject's own model, not recomputed here. */
  stateLabel: z.string().nullable(),
  stateTone: z.enum(['neutral', 'positive', 'caution', 'critical']),
});
export type FavouriteRow = z.infer<typeof FavouriteRow>;

export const WatchlistItem = z.object({
  id: Uuid,
  subject: EntityRef,
  watchedSince: IsoDate,
  watchReason: z.string(),
  /** Null when nothing has changed since `watchedSince` — stated, not zeroed. */
  lastChangeAt: Timestamp.nullable(),
  lastChangeSummary: z.string().nullable(),
  severity: IssueSeverity,
});
export type WatchlistItem = z.infer<typeof WatchlistItem>;

export const WatchlistGroup = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  purpose: z.string(),
  ownerName: z.string(),
  /** Named criteria when the list is rule-driven; empty when hand-curated. */
  rules: z.array(z.string()),
  sharedWith: z.array(z.string()),
  items: z.array(WatchlistItem),
});
export type WatchlistGroup = z.infer<typeof WatchlistGroup>;

export const NotificationChannel = z.enum(['InApp', 'Email', 'Digest']);
export type NotificationChannel = z.infer<typeof NotificationChannel>;

export const NotificationRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  receivedAt: Timestamp,
  channel: NotificationChannel,
  category: z.string(),
  headline: z.string(),
  detail: z.string(),
  severity: IssueSeverity,
  subject: EntityRef.nullable(),
  /**
   * Read state as the server holds it. The client may only change it in
   * response to a deliberate act by the reader: opening a screen is not
   * reading, and a notification silently marked read is a notification lost.
   */
  isRead: z.boolean(),
  readAt: Timestamp.nullable(),
  /** The subscription that produced this — so a reader can mute the cause. */
  ruleCode: z.string(),
  ruleLabel: z.string(),
  /** Where the reader should go to act on it. */
  route: z.string().nullable(),
});
export type NotificationRow = z.infer<typeof NotificationRow>;

export const NotificationFrequency = z.enum(['Immediate', 'DailyDigest', 'WeeklyDigest', 'Off']);
export type NotificationFrequency = z.infer<typeof NotificationFrequency>;

export const SubscriptionRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  label: z.string(),
  event: z.string(),
  scopeLabel: z.string(),
  channels: z.array(NotificationChannel),
  frequency: NotificationFrequency,
  /** Muted subscriptions still fire into the record; they stop reaching you. */
  mutedUntil: IsoDate.nullable(),
  lastFiredAt: Timestamp.nullable(),
  firedLast30Days: z.number().int().nonnegative(),
});
export type SubscriptionRow = z.infer<typeof SubscriptionRow>;

export const WatchlistView = z.object({
  kpis: z.array(MetricValue),
  favourites: z.array(FavouriteRow),
  watchlists: z.array(WatchlistGroup),
  notifications: z.array(NotificationRow),
  subscriptions: z.array(SubscriptionRow),
  /** Notification rules are configured centrally; this view links, not edits. */
  notificationRulesRoute: z.string(),
});
export type WatchlistView = z.infer<typeof WatchlistView>;

// ─────────────────────────────────────────────────────────────────────────────
// 3. Reports & analytics — scheduled and on demand            (UC-13-098)
// ─────────────────────────────────────────────────────────────────────────────

export const ReportRunStatus = z.enum([
  'Succeeded',
  'PartialData',
  'Failed',
  'Running',
  'Cancelled',
]);
export type ReportRunStatus = z.infer<typeof ReportRunStatus>;

export const ReportRunRow = z.object({
  id: Uuid,
  runCode: z.string(),
  reportCode: z.string(),
  reportName: z.string(),
  startedAt: Timestamp,
  /** Null while a run is still going. Not the same as a run that took no time. */
  finishedAt: Timestamp.nullable(),
  durationMs: z.number().int().nonnegative().nullable(),
  status: ReportRunStatus,
  /** What the run produced, in words. Always populated, including on success. */
  outcome: z.string(),
  rowCount: z.number().int().nonnegative().nullable(),
  /**
   * The business date the output describes, which is not the date it ran. A
   * report generated this morning from a feed that last landed on Friday is
   * Friday's report, and saying so is the difference between a stale figure
   * and a wrong one.
   */
  dataAsOf: IsoDate.nullable(),
  triggeredBy: z.string(),
  isScheduled: z.boolean(),
});
export type ReportRunRow = z.infer<typeof ReportRunRow>;

export const ScheduledReportRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  purpose: z.string(),
  audience: z.string(),
  /** Human cadence: "Monday 06:00 UTC, weekly". Not a cron string. */
  cadenceLabel: z.string(),
  nextRunAt: Timestamp.nullable(),
  /** Populated when the schedule is suspended; then `nextRunAt` is null. */
  pausedReason: z.string().nullable(),
  ownerName: z.string(),
  format: z.string(),
  deliveryLabel: z.string(),
  recipientCount: z.number().int().nonnegative(),
  /** Null before the first run has ever completed — stated, never faked. */
  lastRun: ReportRunRow.nullable(),
  /**
   * True when the newest successful output is older than the report's own
   * freshness promise. The UI must not present it as current.
   */
  isStale: z.boolean(),
  staleReason: z.string().nullable(),
});
export type ScheduledReportRow = z.infer<typeof ScheduledReportRow>;

export const OnDemandReportRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  purpose: z.string(),
  parameters: z.array(z.string()),
  estimatedRuntimeLabel: z.string(),
  /** Null when nobody has ever run it. */
  lastRun: ReportRunRow.nullable(),
  ownerName: z.string(),
  /** Restricted reports name the entitlement rather than hiding themselves. */
  restrictedReason: z.string().nullable(),
});
export type OnDemandReportRow = z.infer<typeof OnDemandReportRow>;

export const ReportsAnalyticsView = z.object({
  kpis: z.array(MetricValue),
  scheduled: z.array(ScheduledReportRow),
  onDemand: z.array(OnDemandReportRow),
  recentRuns: z.array(ReportRunRow),
  runOutcomeTrend: ChartPayload,
  deliveryMix: ChartPayload,
  /** Named once, rendered wherever a "Run now" control appears. */
  executionUnavailableReason: z.string(),
});
export type ReportsAnalyticsView = z.infer<typeof ReportsAnalyticsView>;

// ─────────────────────────────────────────────────────────────────────────────
// 4. Taxonomy & policy governance — versions and effective dates (UC-13-094)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Where a version sits in its life.
 *
 * `Scheduled` is deliberately distinct from `InForce`: an approved version with
 * a future effective date governs nothing yet, and a screen that paints the two
 * the same colour will have someone classifying against next quarter's terms.
 */
export const TaxonomyVersionStatus = z.enum([
  'Draft',
  'InReview',
  'Scheduled',
  'InForce',
  'Superseded',
  'Rejected',
]);
export type TaxonomyVersionStatus = z.infer<typeof TaxonomyVersionStatus>;

export const TaxonomyVersionRow = z.object({
  id: Uuid,
  vocabularyCode: z.string(),
  versionLabel: z.string(),
  status: TaxonomyVersionStatus,
  /** Null for drafts: an unapproved version has no effective date to state. */
  effectiveFrom: IsoDate.nullable(),
  effectiveTo: IsoDate.nullable(),
  approvedBy: z.string().nullable(),
  approvedOn: IsoDate.nullable(),
  /** Why approval is still outstanding — shown instead of an empty approver. */
  pendingReason: z.string().nullable(),
  changeCount: z.number().int().nonnegative(),
  summary: z.string(),
});
export type TaxonomyVersionRow = z.infer<typeof TaxonomyVersionRow>;

export const TaxonomyChangeType = z.enum([
  'Added',
  'Renamed',
  'DefinitionChanged',
  'Merged',
  'Deprecated',
  'Removed',
]);
export type TaxonomyChangeType = z.infer<typeof TaxonomyChangeType>;

export const TaxonomyChangeRow = z.object({
  id: Uuid,
  vocabularyCode: z.string(),
  fromVersion: z.string(),
  toVersion: z.string(),
  changeType: TaxonomyChangeType,
  term: z.string(),
  before: z.string().nullable(),
  after: z.string().nullable(),
  /** How many governed records carry the old value today. */
  affectedRecordCount: z.number().int().nonnegative(),
  /** What happens to those records. Null means "nothing is defined yet". */
  migrationRule: z.string().nullable(),
  migrationGapReason: z.string().nullable(),
});
export type TaxonomyChangeRow = z.infer<typeof TaxonomyChangeRow>;

export const TaxonomyVocabularyRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  purpose: z.string(),
  ownerName: z.string(),
  termCount: z.number().int().nonnegative(),
  /** The version governing today's classifications. */
  inForceVersion: z.string(),
  inForceSince: IsoDate,
  /** The next version, if one exists — draft, in review or scheduled. */
  pendingVersion: z.string().nullable(),
  pendingStatus: TaxonomyVersionStatus.nullable(),
  pendingEffectiveFrom: IsoDate.nullable(),
  lastChangedAt: Timestamp,
  /** Entities classified against this vocabulary; the blast radius of a change. */
  governedEntityCount: z.number().int().nonnegative(),
});
export type TaxonomyVocabularyRow = z.infer<typeof TaxonomyVocabularyRow>;

export const PolicyVersionRow = z.object({
  id: Uuid,
  businessCode: z.string(),
  name: z.string(),
  domain: z.string(),
  /** Advisory policies warn; blocking policies refuse. Never rendered alike. */
  enforcement: z.enum(['Blocking', 'Advisory']),
  inForceVersion: z.string(),
  effectiveFrom: IsoDate,
  nextReviewOn: IsoDate.nullable(),
  overdueReviewReason: z.string().nullable(),
  ownerName: z.string(),
  pendingVersion: z.string().nullable(),
  pendingStatus: TaxonomyVersionStatus.nullable(),
  breachCount30Days: z.number().int().nonnegative(),
});
export type PolicyVersionRow = z.infer<typeof PolicyVersionRow>;

export const TaxonomyGovernanceView = z.object({
  kpis: z.array(MetricValue),
  vocabularies: z.array(TaxonomyVocabularyRow),
  versions: z.array(TaxonomyVersionRow),
  changes: z.array(TaxonomyChangeRow),
  policies: z.array(PolicyVersionRow),
  /** Approval of a version happens in the governance console, not here. */
  approvalRoute: z.string(),
  /** The date every "in force" claim on this screen is evaluated against. */
  inForceAsOf: IsoDate,
});
export type TaxonomyGovernanceView = z.infer<typeof TaxonomyGovernanceView>;

// ─────────────────────────────────────────────────────────────────────────────
// 5. The discussion on one record                            (GAP-28)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A comment as it is read beside the thing it is about.
 *
 * Distinct from `CommentRecord` above, which is the row in the cross-estate
 * collaboration register and therefore carries a subject. Here the subject is
 * the page, so it is stated once on the thread rather than repeated on every
 * utterance.
 *
 * `body` is nullable for the same reason it is nullable there: a reader who
 * governs a record must be able to see that a conversation about it exists even
 * when they may not read it, and `withheldReason` says which of the two is
 * happening. Null with no reason never occurs — the route sets them together.
 */
export const DiscussionComment = z.object({
  id: Uuid,
  parentCommentId: Uuid.nullable(),
  /** Null only where the author's directory record has since gone. */
  author: OwnerRef.nullable(),
  authorMissingReason: z.string().nullable(),
  postedAt: Timestamp,
  editedAt: Timestamp.nullable(),
  body: z.string().nullable(),
  withheldReason: z.string().nullable(),
  visibility: CommentVisibility,
  /** Resolved against the directory when the comment was written, not parsed on read. */
  mentions: z.array(OwnerRef),
  resolvedAt: Timestamp.nullable(),
  resolvedBy: z.string().nullable(),
  /**
   * Null when this caller may settle the thread. Otherwise the sentence to show
   * in place of the control — offering a button that will be refused is the
   * same lie as a "Saved" that saved nothing, one step earlier.
   */
  cannotResolveReason: z.string().nullable(),
});
export type DiscussionComment = z.infer<typeof DiscussionComment>;

export const EntityDiscussion = z.object({
  subject: EntityRef,
  comments: z.array(DiscussionComment),
  /** Threads nobody has marked settled. Counted server-side so the badge agrees with the list. */
  openCount: z.number().int().nonnegative(),
  /**
   * People this caller may draw into the thread: everyone who can already reach
   * the record. Mentioning somebody who cannot see the record would send them a
   * notification about something they are not allowed to open.
   */
  mentionCandidates: z.array(OwnerRef),
  /** Null when the caller may post. Otherwise says which entitlement is missing. */
  cannotCommentReason: z.string().nullable(),
});
export type EntityDiscussion = z.infer<typeof EntityDiscussion>;

// ─────────────────────────────────────────────────────────────────────────────
// 6. The notification feed                                   (GAP-29)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What has reached this person, and how much of it they have not looked at.
 *
 * `unreadCount` is counted over the whole feed rather than over `rows`, which
 * is capped: a badge derived from a capped list is a count of the cap. Read
 * state changes only when the reader acts — rendering a notification never
 * marks it read, which is what makes "unread" mean anything.
 */
export const NotificationFeed = z.object({
  rows: z.array(NotificationRow),
  unreadCount: z.number().int().nonnegative(),
  totalRows: z.number().int().nonnegative(),
  hasMore: z.boolean(),
});
export type NotificationFeed = z.infer<typeof NotificationFeed>;
